import React, {
  FunctionComponent, useEffect, useState,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  TargetDomainPrivileges,
} from 'data/targetDomain/targetDomain.types';
import * as actions from 'data/targetDomain/targetDomain.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/targetDomain/targetDomain.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import TargetDomainListGrid from './components/TargetDomainGrid';
import { targetDomainColumnDefs } from '../../TargetDomain.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getTargetDomainList(state),
  selectedTargetDomain: selectors.getSelectedTargetDomain(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToTargetDomainCreate: actions.goToTargetDomainCreate,
  goToTargetDomainEdit: actions.goToTargetDomainEdit,
  goToTargetDomainView: actions.goToTargetDomainView,
  selectTargetDomain: actions.selectTargetDomain,
  getTargetDomainList: actions.getTargetDomainsList,
  deleteTargetDomain: actions.deleteTargetDomain,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const TargetDomainList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedTargetDomain,
  goToTargetDomainCreate,
  goToTargetDomainEdit,
  goToTargetDomainView,
  selectTargetDomain,
  getTargetDomainList,
  deleteTargetDomain,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(targetDomainColumnDefs);

  useEffect(() => {
    getTargetDomainList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getTargetDomainList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getTargetDomainList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToTargetDomainCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectTargetDomain(event.api.getSelectedRows());
  };

  const handleDeleteTargetDomain = () => {
    const distributorConfigrationId = selectedTargetDomain[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedTargetDomain[0]?.domain,
        AuditService.TARGET_DOMAIN,
        AuditActionType.TARGET_DOMAIN_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteTargetDomain(deliveryFunctionId, distributorConfigrationId, body);
  };

  const handleGoToEdit = () => goToTargetDomainEdit(history);

  const handleGoToView = () => goToTargetDomainView(history);

  const hasPrivilegeToView = privileges.includes(TargetDomainPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(TargetDomainPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(TargetDomainPrivileges.Delete);

  return (
    <div data-testid="TargetDomainList" className="GridPageContainer">
      <TargetDomainListGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedTargetDomain={selectedTargetDomain[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteTargetDomain}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </div>
  );
};

export default withRouter(connector(TargetDomainList));
